import { useEffect, useState } from "react";
import { Command } from "cmdk";
import { cn } from "../../lib/utils";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  Home01Icon,
  TaskDaily02Icon,
  Route01Icon,
  Calendar03Icon,
  Target02Icon,
  Megaphone01Icon,
  UserMultiple02Icon,
  ChartLineData02Icon,
  CheckListIcon,
  Folder01Icon,
  Search01Icon,
  KeyboardIcon,
} from "@hugeicons/core-free-icons";
import { IconMagnifier } from "nucleo-glass";
import { NucleoIcon } from "./NucleoIcon";
import { routes, type RouteId } from "../../lib/routes";
import { getShortcutsList } from "../../hooks/useShortcuts";

const iconMap: Record<string, typeof Home01Icon> = {
  Home01Icon,
  TaskDaily02Icon,
  Route01Icon,
  Calendar03Icon,
  Target02Icon,
  Megaphone01Icon,
  UserMultiple02Icon,
  ChartLineData02Icon,
  CheckListIcon,
  Folder01Icon,
};

const ROUTE_SHORTCUT_KEY: Record<RouteId, string> = {
  dashboard: "1",
  board: "2",
  path: "3",
  timeline: "4",
  pipeline: "5",
  channels: "6",
  contacts: "7",
  scorecard: "8",
  checklist: "9",
  assets: "0",
};

interface CommandPaletteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onNavigate: (route: RouteId) => void;
  onToggleSidebar: () => void;
  onShowShortcuts: () => void;
  onCreateTask: () => void;
  onFilterOwner: (owner: "Joel" | "Albert" | "Joint" | "all") => void;
  onArchiveCompleted: () => void;
}

const groupHeadingCls = "[&_[cmdk-group-heading]]:text-[11px] [&_[cmdk-group-heading]]:font-semibold [&_[cmdk-group-heading]]:text-[var(--color-text-muted)] [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-[0.06em] [&_[cmdk-group-heading]]:px-[var(--spacing-2)] [&_[cmdk-group-heading]]:py-[var(--spacing-1)]";

const itemCls = cn(
  "flex items-center gap-[var(--spacing-3)] rounded-[var(--radius-md)]",
  "px-[var(--spacing-3)] py-[var(--spacing-2)] cursor-pointer",
  "text-[14px] text-[var(--color-text-secondary)]",
  "data-[selected=true]:bg-[var(--color-bg-hover)] data-[selected=true]:text-[var(--color-text-primary)]",
  "transition-colors duration-[var(--duration-fast)]",
);

export function CommandPalette({
  open,
  onOpenChange,
  onNavigate,
  onToggleSidebar,
  onShowShortcuts,
  onCreateTask,
  onFilterOwner,
  onArchiveCompleted,
}: CommandPaletteProps) {
  const [search, setSearch] = useState("");

  // Reset search when opened
  useEffect(() => {
    if (open) setSearch("");
  }, [open]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 animate-fade-in"
        onClick={() => onOpenChange(false)}
      />

      {/* Palette */}
      <div className="absolute left-1/2 top-[20%] -translate-x-1/2 w-full max-w-[520px] animate-stagger-in">
        <Command
          className={cn(
            "rounded-[var(--radius-lg)] border border-[var(--color-border-stroke)]",
            "bg-[var(--color-bg-surface)] shadow-[var(--shadow-modal)]",
            "overflow-hidden",
          )}
          onKeyDown={(e) => {
            if (e.key === "Escape") onOpenChange(false);
          }}
          loop
        >
          {/* Input */}
          <div className="flex items-center gap-[var(--spacing-3)] px-[var(--spacing-4)] border-b border-[var(--color-border-stroke)]">
            <HugeiconsIcon icon={Search01Icon} size={16} className="text-[var(--color-text-muted)] shrink-0" />
            <Command.Input
              value={search}
              onValueChange={setSearch}
              placeholder="Type a command or search…"
              className={cn(
                "flex-1 h-[48px] bg-transparent",
                "text-[14px] text-[var(--color-text-primary)] placeholder:text-[var(--color-text-muted)]",
                "outline-none border-none ring-0 shadow-none",
                "[&:focus-visible]:outline-none [&:focus-visible]:ring-0 [&:focus-visible]:shadow-none",
              )}
              autoFocus
            />
            <kbd className="text-[11px] text-[var(--color-text-muted)] bg-[var(--color-bg-hover)] rounded-[4px] px-[6px] py-[2px] font-mono">
              ESC
            </kbd>
          </div>

          {/* Results */}
          <Command.List
            className="max-h-[320px] overflow-y-auto p-[var(--spacing-2)]"
          >
            <Command.Empty className="text-[14px] text-[var(--color-text-muted)] text-center py-[var(--spacing-6)] flex flex-col items-center gap-[var(--spacing-2)]">
              <NucleoIcon icon={<IconMagnifier size={24} />} className="opacity-40" />
              No results found.
            </Command.Empty>

            {/* Navigation */}
            <Command.Group heading="Navigation" className={groupHeadingCls}>
              {routes.map((route) => {
                const IconComponent = iconMap[route.iconName];
                return (
                  <Command.Item
                    key={route.id}
                    value={`go to ${route.label}`}
                    onSelect={() => {
                      onNavigate(route.id);
                      onOpenChange(false);
                    }}
                    className={itemCls}
                  >
                    {IconComponent && <HugeiconsIcon icon={IconComponent} size={16} className="shrink-0" />}
                    <span className="flex-1">{route.label}</span>
                    <kbd className="text-[11px] text-[var(--color-text-muted)] font-mono">{ROUTE_SHORTCUT_KEY[route.id]}</kbd>
                  </Command.Item>
                );
              })}
            </Command.Group>

            {/* Actions */}
            <Command.Group heading="Actions" className={cn("mt-[var(--spacing-2)]", groupHeadingCls)}>
              <Command.Item
                value="toggle sidebar"
                onSelect={() => { onToggleSidebar(); onOpenChange(false); }}
                className={itemCls}
              >
                <span className="flex-1">Toggle sidebar</span>
                <kbd className="text-[11px] text-[var(--color-text-muted)] font-mono">⌘ B</kbd>
              </Command.Item>

              <Command.Item
                value="keyboard shortcuts help"
                onSelect={() => { onShowShortcuts(); onOpenChange(false); }}
                className={itemCls}
              >
                <HugeiconsIcon icon={KeyboardIcon} size={16} className="shrink-0" />
                <span className="flex-1">Keyboard shortcuts</span>
                <kbd className="text-[11px] text-[var(--color-text-muted)] font-mono">?</kbd>
              </Command.Item>
            </Command.Group>

            {/* Task Actions */}
            <Command.Group heading="Task Actions" className={cn("mt-[var(--spacing-2)]", groupHeadingCls)}>
              <Command.Item
                value="create new task"
                onSelect={() => { onCreateTask(); onOpenChange(false); }}
                className={itemCls}
              >
                <span className="flex-1">Create new task</span>
                <kbd className="text-[11px] text-[var(--color-text-muted)] font-mono">N</kbd>
              </Command.Item>

              <Command.Item
                value="archive all completed tasks"
                onSelect={() => { onArchiveCompleted(); onOpenChange(false); }}
                className={itemCls}
              >
                <span className="flex-1">Archive all completed tasks</span>
              </Command.Item>
            </Command.Group>

            {/* Quick Filters */}
            <Command.Group heading="Quick Filters" className={cn("mt-[var(--spacing-2)]", groupHeadingCls)}>
              <Command.Item value="show Joel's tasks" onSelect={() => { onFilterOwner("Joel"); onOpenChange(false); }} className={itemCls}>
                <span className="flex-1">Show Joel&apos;s tasks</span>
              </Command.Item>
              <Command.Item value="show Albert's tasks" onSelect={() => { onFilterOwner("Albert"); onOpenChange(false); }} className={itemCls}>
                <span className="flex-1">Show Albert&apos;s tasks</span>
              </Command.Item>
              <Command.Item value="show Joint tasks" onSelect={() => { onFilterOwner("Joint"); onOpenChange(false); }} className={itemCls}>
                <span className="flex-1">Show Joint tasks</span>
              </Command.Item>
              <Command.Item value="clear all filters" onSelect={() => { onFilterOwner("all"); onOpenChange(false); }} className={itemCls}>
                <span className="flex-1">Clear all filters</span>
              </Command.Item>
            </Command.Group>
          </Command.List>

          {/* Footer */}
          <div className="flex items-center gap-[var(--spacing-4)] px-[var(--spacing-4)] py-[var(--spacing-2)] border-t border-[var(--color-border-stroke)] text-[11px] text-[var(--color-text-muted)]">
            <span className="flex items-center gap-[var(--spacing-1)]">
              <kbd className="bg-[var(--color-bg-hover)] rounded-[3px] px-[4px] py-[1px] font-mono">↑↓</kbd> navigate
            </span>
            <span className="flex items-center gap-[var(--spacing-1)]">
              <kbd className="bg-[var(--color-bg-hover)] rounded-[3px] px-[4px] py-[1px] font-mono">↵</kbd> select
            </span>
            <span className="flex items-center gap-[var(--spacing-1)]">
              <kbd className="bg-[var(--color-bg-hover)] rounded-[3px] px-[4px] py-[1px] font-mono">esc</kbd> close
            </span>
          </div>
        </Command>
      </div>
    </div>
  );
}
